import AddActionButton from '@/components/AddActionButton';
import CaptureList from '@/components/CaptureList';
import { DroppableContainer } from '@/components/DragAndDrop';
import EmptyActionList from '@/components/EmptyActionList';
import MasonryContainer from '@/components/Masonry';
import QuickActionCreator from '@/components/QuickActionCreator';
import useCaptureListData from '@/hooks/useCaptureListData';
import { Action } from '@/types/actions';
import { Category } from '@/types/category';
import rem from '@/utils/rem';
import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Text,
  VStack,
} from '@chakra-ui/react';
import { motion, useAnimation } from 'framer-motion';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type CategoryWithActions = {
  completedActions: Action[];
  incompleteActions: Action[];
} & Category;

type EmptyCategoriesProps = {
  draggable?: boolean;
  categories?: Category[];
  viewportRef: React.MutableRefObject<HTMLDivElement | null>;
  columnCount?: number;
  openCreateBlockModal?: () => void;
  setActionModalOpen?: (isOpen: boolean, props?: any) => void;
  setTempCategoryId?: (categoryId: string) => void;
  setQuickActionOpen?: (categoryId: string) => void;
  showCompletedActions?: boolean;
  showQuickActionCreation?: { show: boolean; selectedCategory: string };
};

const EmptyCategories = ({
  categories,
  viewportRef,
  columnCount = 1,
  openCreateBlockModal,
  setActionModalOpen,
  setTempCategoryId,
  setQuickActionOpen,
  draggable,
  showQuickActionCreation,
}: EmptyCategoriesProps) => {
  const { emptyCategories: captureListEmptyCategories } = useCaptureListData();
  const { t } = useTranslation('EmptyCategories');
  const { t: tEmptyActionList } = useTranslation('EmptyActionList');
  const emptyCategories = categories
    ? categories.filter((c) => !c.isArchived)
    : captureListEmptyCategories.filter((c) => !c.isArchived);
  const actionCreatorOptionsAnimation = useAnimation();

  const SingleCategoryContainer = useCallback(
    (category: CategoryWithActions) => {
      const quickActionOpen = showQuickActionCreation?.show && showQuickActionCreation.selectedCategory === category.id;
      return (
        <DroppableContainer
          id={category?.id}
          key={category?.id}
          payload={{
            categoryId: category?.id,
          }}
          as={VStack}
          alignItems="flex-start"
          width="full"
          marginBottom={rem(16)}
          borderRadius={rem(16)}
        >
          <CaptureList
            key={`${category.id}`}
            category={category}
            showDurationTimers={false}
            marginBottom={rem(16)}
            draggableCategory={draggable}
            addEntityButton={
              <AddActionButton
                showMinusIcon={quickActionOpen}
                backgroundColor={category?.color}
                withActionBlockCreationMenu={!!openCreateBlockModal && !!setTempCategoryId}
                onExternalClick={() => {
                  setQuickActionOpen
                    ? setQuickActionOpen(category.id)
                    : setActionModalOpen
                      ? setActionModalOpen(true, { categoryId: category?.id, location: 'empty categories' })
                      : undefined;
                }}
                onCreateNewBlock={() => {
                  !!setTempCategoryId && setTempCategoryId(category.id);
                  !!openCreateBlockModal && openCreateBlockModal();
                }}
                color="text-primary"
                textColor="text-primary"
                iconSize={rem(10)}
                minWidth={rem(24)}
                width={rem(24)}
                height={rem(24)}
                tooltipText={
                  quickActionOpen ? t('tooltips.hideQuickActionCreator') : t('tooltips.showQuickActionCreator')
                }
              />
            }
          >
            {quickActionOpen && (
              <Box marginBottom={3}>
                <QuickActionCreator
                  category={category}
                  selectedCategoryId={category?.id}
                  actionCreatorOptionsAnimation={actionCreatorOptionsAnimation}
                />
              </Box>
            )}
            <EmptyActionList
              title={tEmptyActionList('titles.noCurrentActions')}
              message={tEmptyActionList('messages.clickToAddAction')}
              motivational="small"
            />
          </CaptureList>
        </DroppableContainer>
      );
    },
    [
      openCreateBlockModal,
      setActionModalOpen,
      setTempCategoryId,
      setQuickActionOpen,
      t,
      tEmptyActionList,
      draggable,
      actionCreatorOptionsAnimation,
      showQuickActionCreation,
    ],
  );

  return (
    <AccordionItem marginTop={rem(24)}>
      <motion.div
        initial="hidden"
        whileInView="visible"
        transition={{ duration: 0.5 }}
        viewport={{ root: viewportRef }}
        variants={{
          visible: { opacity: 1 },
          hidden: { opacity: 0 },
        }}
      >
        <Flex alignItems="center" justifyContent="space-between" minHeight={rem(32)} marginTop={rem(21)}>
          <AccordionButton flex="1" padding="0">
            <Text textStyle="large">{t('titles.emptyCategories')}</Text>
            <AccordionIcon marginLeft={rem(8)} />
          </AccordionButton>
        </Flex>
      </motion.div>

      <AccordionPanel marginTop={rem(24)} padding="0">
        <MasonryContainer
          dataArray={emptyCategories}
          columnCount={columnCount}
          renderChild={(category) => SingleCategoryContainer(category as CategoryWithActions)}
        />
      </AccordionPanel>
    </AccordionItem>
  );
};

export default EmptyCategories;
